import React, { useEffect, useState } from 'react';
import { Alert, Box, FormControlLabel, Switch, MenuItem, TextField, Typography } from '@mui/material';
import { api } from '../../utils/apiClient';
import ActionButton from '../common/ActionButton';

export default function ActiveMetricsProvider({ projectId, editable, initialProvider = 'prometheus', initialEnabled = true, onProviderSaved }: { projectId: string; editable: boolean; initialProvider?: string; initialEnabled?: boolean; onProviderSaved?: (provider: string, enabled: boolean) => void }) {
  const path = `/settings?project_id=${encodeURIComponent(projectId)}`;
  const [saved, setSaved] = useState(initialProvider);
  const [choice, setChoice] = useState(initialProvider);
  const [enabled, setEnabled] = useState(initialEnabled);
  const [savedEnabled, setSavedEnabled] = useState(initialEnabled);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [readiness, setReadiness] = useState('');
  useEffect(() => {
    setSaved(initialProvider);
    setSavedEnabled(initialEnabled);
  }, [initialProvider, initialEnabled]);
  const refresh = async () => {
    const status = await api.get<{ provider: string; readiness: string }>('/observability/status?project_id=' + encodeURIComponent(projectId));
    setReadiness(`${status.provider}: ${status.readiness}`);
  };
  const save = async () => {
    setBusy(true); setError(''); setReadiness('');
    try {
      await api.put(path, { activeMetricsProvider: choice, observabilityEnabled: enabled });
      const result = await api.get<{ activeMetricsProvider: string; observabilityEnabled: boolean }>(path);
      if (result.activeMetricsProvider !== choice || result.observabilityEnabled !== enabled) throw new Error();
      setSaved(result.activeMetricsProvider);
      setSavedEnabled(result.observabilityEnabled);
      onProviderSaved?.(result.activeMetricsProvider, result.observabilityEnabled);
      try { await refresh(); } catch { setError('Provider saved; runtime evidence check could not complete.'); }
    } catch { setError('Provider save could not be verified. Reload settings before retrying.'); }
    finally { setBusy(false); }
  };
  return <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle1">Saved active provider: {saved} · Runtime metrics {savedEnabled ? 'enabled' : 'disabled'}</Typography>
    <FormControlLabel label="Use runtime metrics in selection" control={<Switch checked={enabled} disabled={!editable || busy} onChange={event => setEnabled(event.target.checked)} />} />
    {(choice !== saved || enabled !== savedEnabled) && <Alert severity="info">Unsaved runtime metrics choices</Alert>}
    <Typography variant="body2">When enabled, only the saved selected provider supplies selection metrics. Saved connections are retained.</Typography>
    <TextField select label="Active metrics provider" value={choice} disabled={!editable || busy || !saved} onChange={event => setChoice(event.target.value)} sx={{ my: 2, minWidth: 220 }}>
      <MenuItem value="prometheus">Prometheus</MenuItem><MenuItem value="dynatrace">Dynatrace</MenuItem>
    </TextField>
    <ActionButton disabled={!editable || busy || !saved} onClick={save} sx={{ m: 2 }}>Save runtime settings</ActionButton>
    {readiness && <Alert severity="info">Runtime evidence: {readiness}</Alert>}
    {error && <Alert severity="error">{error}</Alert>}
  </Box>;
}
